import { getIn, useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { useModal } from '@context/ModalsContext'

import { Input } from '@ui/components/Input'
import { BaseInputButton, CountrySelector } from '@ui/components/Input/BaseInputButton'
import { DummyIcon } from '@ui/icons/Dummy.icon'
import { FlagIcon } from '@ui/icons/flags'

import { createInputPhoneNumberFilter } from '@utils/formValidation'
import { preventPaste } from '@utils/input.utils'

import { CountryPreferenceStrippedDto } from '@model/CountryDto'

import { getFieldValid } from '@global/formField/helpers'

import { useGeolocationCountryCode } from '@hooks/useGeolocationCountryCode'

import { PhoneCodeModal } from '@shared/modals/PhoneCode.modal'

interface PhoneNumberInputProps {
  name: string
  handleChangePhoneNumber: (e: React.ChangeEvent<HTMLInputElement>) => void
  values: string
  countryCode: string
  countries: CountryPreferenceStrippedDto[]
  initialCountry: CountryPreferenceStrippedDto | undefined
  isCountryDropdownOpen?: boolean
  setIsCountryDropdownOpen?: (isOpen: boolean) => void
  handleOnChangeOption: (country: CountryPreferenceStrippedDto) => void
  phoneNumberError: string | undefined
  dropdownRef?: React.RefObject<HTMLDivElement | null>
  hint?: string | React.ReactNode
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  name,
  handleChangePhoneNumber,
  values = '',
  countryCode,
  countries = [],
  initialCountry = null,
  isCountryDropdownOpen = false,
  setIsCountryDropdownOpen,
  handleOnChangeOption,
  phoneNumberError,
  hint,
  onBlur,
  dropdownRef,
}) => {
  const { t } = useTranslation()
  const [selectedCountry, setSelectedCountry] = useState<CountryPreferenceStrippedDto | null>(
    initialCountry
  )

  const { countryCode: geolocationCountryCode, isLoading: isGeolocationLoading } =
    useGeolocationCountryCode(countries || [], !countryCode)

  const context = useFormikContext<any>()

  useEffect(() => {
    if (geolocationCountryCode && !countryCode && countries) {
      const country = countries.find((c) => c.phoneCode === geolocationCountryCode)
      if (country) {
        setSelectedCountry(country)
        handleOnChangeOption(country)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geolocationCountryCode, countryCode])

  const label = `${t('PhoneNumbers.Phone number')} *`
  const placeholder = `${t('PhoneNumbers.Phone number')} *`

  const labelProps = { label, placeholder }

  const phoneCodeModal = useModal(PhoneCodeModal, {
    options: countries,
    initialValue: selectedCountry?.country.id,
    onChangeOption: (v) => {
      handleOnChangeOption(v)
      setSelectedCountry(v)
    },
  })

  const showPlaceholder = isGeolocationLoading && !countryCode && !selectedCountry

  const touched = context ? getIn(context.touched, name) : false
  const formikError = context ? getIn(context.errors, name) : undefined

  const finalError = touched ? phoneNumberError || formikError : undefined
  const isValid = getFieldValid(finalError, touched, !!values)

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (context) {
      context.setFieldTouched(name, true)
    }
    onBlur?.(e)
  }

  return (
    <Input
      variant='advanced-with-button'
      onInput={createInputPhoneNumberFilter()}
      min={0}
      type='tel'
      fullWidth
      inputMode='numeric'
      name={name}
      {...labelProps}
      value={values}
      onChange={handleChangePhoneNumber}
      onBlur={handleBlur}
      onPaste={preventPaste}
      autoComplete={`${name}-${uuid()}`}
      buttonComponent={
        <BaseInputButton
          size='xl'
          text={countryCode || t('Sign up.Country Code')}
          startElement={
            showPlaceholder || !selectedCountry ? (
              <DummyIcon size={28} />
            ) : (
              <FlagIcon size={28} countryCode={selectedCountry.country.id} />
            )
          }
          showDropdown={isCountryDropdownOpen}
          onToggleDropdown={() =>
            setIsCountryDropdownOpen && setIsCountryDropdownOpen(!isCountryDropdownOpen)
          }
          onToggleModal={() => phoneCodeModal.open()}
          dropdownRef={dropdownRef}
          dropdownContent={
            <CountrySelector
              countries={countries}
              selectedCountry={selectedCountry}
              onCountrySelect={(v) => {
                handleOnChangeOption(v)
                setSelectedCountry(v)
              }}
              isOpen={isCountryDropdownOpen}
              onClose={() => setIsCountryDropdownOpen && setIsCountryDropdownOpen(false)}
            />
          }
        />
      }
      error={!isValid}
      hint={finalError ?? hint}
      size='xl'
    />
  )
}
